import { useMemo } from 'react';
import {
  PageHeader,
  DashboardGrid,
  DashboardCard,
  MetricCard,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { IndianRupee, FileText, HandCoins, TrendingUp } from 'lucide-react';
import { useKoshDashboard, useKoshLabels, useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { formatMoney, formatPercent } from '@/lib/format';

const AGING_LABEL: Record<string, string> = {
  CURRENT: 'Current',
  D1_30: '1–30 days',
  D31_60: '31–60 days',
  D61_90: '61–90 days',
  D90_PLUS: '90+ days',
};

export function DashboardPage() {
  const q = useKoshDashboard();
  const { verticalName, labelPlural } = useKoshLabels();
  const nameMap = useCustomerNameMap();

  const ccy = q.data?.revenue.currency ?? 'INR';
  const cards = useMemo(() => {
    const d = q.data;
    return [
      {
        label: `Invoiced (${d?.revenue.invoiceCount ?? 0} ${labelPlural('invoice').toLowerCase()})`,
        value: formatMoney(d?.revenue.invoicedTotal, ccy),
        icon: IndianRupee,
        tone: 'neutral' as const,
      },
      {
        label: 'Collected',
        value: formatMoney(d?.collections.collected, ccy),
        icon: HandCoins,
        tone: 'success' as const,
      },
      {
        label: 'Outstanding',
        value: formatMoney(d?.collections.outstandingBalance, ccy),
        icon: FileText,
        tone:
          (d?.collections.outstandingBalance ?? 0) > 0 ? ('warning' as const) : ('neutral' as const),
      },
      {
        label: 'Collection rate',
        value: formatPercent(d?.collections.collectionRate),
        icon: TrendingUp,
        tone: 'info' as const,
      },
    ];
  }, [q.data, ccy, labelPlural]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={`${verticalName} · Financial visibility`}
        title="Dashboard"
        description="Revenue, collections, aging and exposure across your receivables."
      />

      <QueryStateGuard
        data={q.data}
        isLoading={q.isLoading}
        isError={q.isError}
        emptyTitle="No financial data yet"
        emptyDescription="Issue your first invoice to see revenue and collections here."
      >
        {(d) => (
          <div className="space-y-6">
            <DashboardGrid columns={4}>
              {cards.map((c) => (
                <MetricCard key={c.label} label={c.label} value={c.value} icon={c.icon} tone={c.tone} />
              ))}
            </DashboardGrid>

            <DashboardGrid columns={2}>
              <DashboardCard title="Aging" description="Open balance by age bucket">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bucket</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.aging.map((a) => (
                      <TableRow key={a.bucket}>
                        <TableCell>{AGING_LABEL[a.bucket] ?? a.bucket}</TableCell>
                        <TableCell className="text-right tabular-nums">{a.count}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatMoney(a.balance, ccy)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DashboardCard>

              <DashboardCard
                title={`Top ${labelPlural('customer').toLowerCase()} exposure`}
                description="Largest open balances"
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{labelPlural('customer').replace(/s$/, '')}</TableHead>
                      <TableHead className="text-right">Open items</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.topExposure.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground">
                          No open exposure.
                        </TableCell>
                      </TableRow>
                    ) : (
                      d.topExposure.map((e) => (
                        <TableRow key={e.customerId}>
                          <TableCell>{nameMap.get(e.customerId) ?? e.customerId.slice(0, 8)}</TableCell>
                          <TableCell className="text-right tabular-nums">{e.openItems}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(e.outstandingBalance, ccy)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </DashboardCard>
            </DashboardGrid>

            <DashboardCard title={`${labelPlural('invoice')} by status`}>
              <div className="flex flex-wrap gap-3">
                {d.invoiceStatus.map((s) => (
                  <div key={s.status} className="rounded-lg border border-border bg-card px-4 py-3">
                    <div className="text-[11.5px] font-medium capitalize text-muted-foreground">
                      {s.status.replace(/_/g, ' ').toLowerCase()}
                    </div>
                    <div className="mt-0.5 text-lg font-semibold tabular-nums">{s.count}</div>
                    <div className="text-[11px] text-muted-foreground tabular-nums">
                      {formatMoney(s.value, ccy)}
                    </div>
                  </div>
                ))}
              </div>
            </DashboardCard>
          </div>
        )}
      </QueryStateGuard>
    </div>
  );
}
